import { JsonApiRequestError, Mutation } from '@farfetched/core';
import { chain } from '@leprecoins/effector';
import { sample, Store } from 'effector';
import { Voucher, createEditVoucherMutation, EditVoucherMutationParams } from 'shared/api';
import {
	createModalFormModel,
	greaterZeroRule,
	ModalFormModel,
	notEmptyRule,
	notify,
} from 'shared/lib';

export type EditVoucherFormValue = {
	deposit: string;
	wagering: string;
};

export type EditVoucherModel = ModalFormModel<Voucher, EditVoucherFormValue> & {
	editVoucherMutation: Mutation<EditVoucherMutationParams, Voucher, JsonApiRequestError>;
	$pending: Store<boolean>;
};

export const createEditVoucherModel = (): EditVoucherModel => {
	const editVoucherMutation = createEditVoucherMutation();

	const formModel = createModalFormModel<Voucher, EditVoucherFormValue>({
		fields: {
			deposit: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
			wagering: {
				init: '',
				rules: [notEmptyRule, greaterZeroRule],
			},
		},
		resetErrorsOnChange: true,
		validateOn: ['submit'],
	});

	sample({
		clock: formModel.modalButtonClicked,
		fn: (voucher) => ({
			deposit: voucher.deposit.toString(),
			wagering: voucher.wagering.toString(),
		}),
		target: [formModel.form.setInitialValues, formModel.form.setValues],
	});

	sample({
		clock: formModel.form.submitted,
		source: { form: formModel.form.$values, voucher: formModel.$modalData },
		filter: ({ voucher }) => voucher !== null,
		fn: ({ form, voucher }) => ({
			id: voucher!.id,
			deposit: Number(form.deposit),
			wagering: Number(form.wagering),
			isLocked: voucher!.isLocked,
		}),
		target: editVoucherMutation.start,
	});

	notify(editVoucherMutation.finished.success, 'edit-voucher.success');

	chain(editVoucherMutation.finished.success, formModel.switcher.disable);

	sample({
		clock: formModel.modalClosed,
		fn: () => ({
			deposit: '',
			wagering: '',
		}),
		target: [formModel.form.setInitialValues, formModel.form.setValues],
	});

	return {
		editVoucherMutation,
		$pending: editVoucherMutation.$pending,
		...formModel,
	};
};
